/* eslint-disable react/no-array-index-key */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

function PINInput({ PIN, setPIN, disabled, onFilled, onChange }) {
  const [activeInput, setActiveInput] = useState(0);

  const inputRef = useRef([]);

  useEffect(() => {
    let shouldCallOnFilled = true;

    onChange(PIN.join(''));

    PIN.every(digit => {
      if (digit === '') {
        shouldCallOnFilled = false;
        return false;
      }
      return true;
    });

    if (shouldCallOnFilled) {
      onFilled(PIN.join(''));
    }
  }, [PIN]);

  useEffect(() => {
    // We have to use setTimeout here, because the inputRef is not set immediately.
    setTimeout(() => {
      inputRef.current[activeInput]?.focus();
    }, 10);
  }, [activeInput]);

  const handleChange = useCallback((event, index) => {
    const { value } = event.target;

    if (value.length > 1) {
      return;
    }

    setPIN(prevState => {
      const newState = [...prevState];
      newState[index] = value;
      return newState;
    });

    if (value !== '') {
      setActiveInput(index + 1);
    }
  }, []);

  const handleKeyDown = useCallback(({ key }, index) => {
    if (key === 'Tab') {
      if (index === PIN.length - 1) {
        setActiveInput(0);
      } else {
        setActiveInput(index + 1);
      }
    }

    if (key === 'Backspace') {
      setPIN(prevState => {
        const newState = [...prevState];
        newState[index] = '';
        return newState;
      });

      if (index > 0) {
        setActiveInput(index - 1);
      }
    }
  }, []);

  const handlePaste = useCallback(event => {
    event.preventDefault();

    const pastedValue = event.clipboardData.getData('text/plain');

    if (pastedValue.length <= PIN.length) {
      setPIN(prevState => {
        const newState = [...prevState];
        for (let i = 0; i < pastedValue.length; i++) {
          newState[i] = pastedValue[i];
        }
        return newState;
      });
      setActiveInput(pastedValue.length);
    }
  }, []);

  return (
    <div className="flex items-center space-x-4">
      {PIN.map((value, index) => {
        return (
          <input
            className="h-12 w-8 appearance-none border-0 bg-transparent text-center text-base text-black ring-1 ring-gray transition focus:outline-none focus:ring-black disabled:opacity-50 md:text-sm"
            disabled={disabled}
            key={index}
            ref={element => (inputRef.current[index] = element)}
            inputMode="numeric"
            pattern="[0-9]*"
            onChange={event => handleChange(event, index)}
            onKeyDown={event => handleKeyDown(event, index)}
            onPaste={event => handlePaste(event)}
            value={PIN[index]}
          />
        );
      })}
    </div>
  );
}

PINInput.propTypes = {
  /**
   * The code to be displayed in the PIN input. The length of the array determines the number of
   * inputs.
   */
  PIN: PropTypes.arrayOf(PropTypes.string).isRequired,
  /**
   * The function to be called when the code changes.
   */
  setPIN: PropTypes.func.isRequired,
  /**
   * Determines whether the PIN Input is disabled or not.
   */
  disabled: PropTypes.bool,
  /**
   * Callback function that will be called when the PIN changes. The callback will be called with
   * the new PIN as the first argument.
   */
  onChange: PropTypes.func,
  /**
   * Callback function that will be called when the PIN is filled. The callback will be called with
   * the new PIN as the first argument.
   */
  onFilled: PropTypes.func
};

PINInput.defaultProps = {
  disabled: false,
  onFilled: () => {},
  onChange: () => {}
};

export default PINInput;
